import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ZonaCargando from "../alertas/zonaCargando";
import { formatearMoneda } from "../../funcionesDatos/Moneda";

function fmtM(n) { return formatearMoneda(Number(n || 0), "COP"); }
function fmtF(f) {
    if (!f) return "—";
    return new Date(f).toLocaleString("es-CO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function val(v) { return (!v || v === "NA" || v === "0" || v === 0) ? null : v; }

const ESTADO_RECLAMO = {
    abierto:                    { label: "Abierto",              cls: "bg-danger-subtle text-danger-emphasis border border-danger-subtle",         icono: "bi-exclamation-circle-fill" },
    en_revision:                { label: "En revisión",          cls: "bg-warning-subtle text-warning-emphasis border border-warning-subtle",       icono: "bi-search"                  },
    resuelto_a_favor_comprador: { label: "Resuelto → Comprador", cls: "bg-primary-subtle text-primary-emphasis border border-primary-subtle",       icono: "bi-person-check-fill"       },
    resuelto_a_favor_proveedor: { label: "Resuelto → Vendedor",  cls: "bg-success-subtle text-success-emphasis border border-success-subtle",       icono: "bi-person-fill-check"       },
    cerrado:                    { label: "Cerrado",               cls: "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle", icono: "bi-check-circle"            },
};

const ESTADO_DESEMBOLSO = {
    pendiente:  { label: "Pendiente",  cls: "bg-warning-subtle text-warning-emphasis border border-warning-subtle"   },
    programado: { label: "Programado", cls: "bg-primary-subtle text-primary-emphasis border border-primary-subtle"   },
    pagado:     { label: "Pagado",     cls: "bg-success-subtle text-success-emphasis border border-success-subtle"   },
    retenido:   { label: "Retenido",   cls: "bg-danger-subtle text-danger-emphasis border border-danger-subtle"      },
    fallido:    { label: "Fallido",    cls: "bg-danger-subtle text-danger-emphasis border border-danger-subtle"      },
    cancelado:  { label: "Cancelado",  cls: "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle" },
};

function Fila({ label, children, value }) {
    const contenido = children || (value ? <span className="small fw-semibold text-dark">{value}</span> : null);
    if (!contenido) return null;
    return (
        <div className="d-flex justify-content-between align-items-start py-2 border-bottom" style={{ borderColor: "var(--color-track-bg)" }}>
            <span className="text-muted small">{label}</span>
            {contenido}
        </div>
    );
}

function TarjetaUsuario({ u, rol }) {
    if (!u) return null;
    const nombre = [u.nombre, u.apellido].filter(Boolean).join(" ") || u.username || "—";
    return (
        <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-3">
                <p className="text-muted small fw-semibold text-uppercase mb-2" style={{ letterSpacing: "0.05em", fontSize: "0.72rem" }}>{rol}</p>
                <div className="d-flex align-items-center gap-3 mb-2">
                    <img src={u.avatar || "/imagenes/marca/avatar.png"} alt={nombre} className="rounded-circle flex-shrink-0" style={{ width: 40, height: 40, objectFit: "cover" }} />
                    <div>
                        <div className="fw-semibold text-dark small">{nombre}</div>
                        <div className="text-muted" style={{ fontSize: "0.72rem" }}>@{u.username || "—"} · {u.email || "—"}</div>
                    </div>
                </div>
                {u.username && (
                    <button type="button" className="btn btn-sm btn-outline-primary rounded-pill px-3 w-100"
                        onClick={() => {
                            localStorage.setItem("admin_nav_usuario", JSON.stringify({ username: u.username }));
                            window.open("/admin/usuario", "_blank");
                        }}>
                        <i className="bi bi-person-badge me-1"></i>Ver perfil
                    </button>
                )}
            </div>
        </div>
    );
}

function ReclamoIndividual() {
    const location = useLocation();
    const navigate = useNavigate();
    const { idReserva } = location.state?.params || {};

    const [reserva, setReserva]     = useState(null);
    const [comprador, setComprador] = useState(null);
    const [proveedor, setProveedor] = useState(null);
    const [servicio, setServicio]   = useState(null);
    const [cargando, setCargando]   = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [textoRes, setTextoRes]   = useState("");

    async function cargar() {
        try {
            setCargando(true);
            const res = await axios.post("/obtener-reserva-admin-completa", { params: { idReserva } });
            if (!res.data.exito) {
                Swal.fire({ icon: "error", title: "Error", text: res.data.mensaje || "No se pudo cargar.", confirmButtonColor: "var(--azul-logo-fondo)" });
                return navigate(-1);
            }
            setReserva(res.data.reservaActual);
            setComprador(res.data.comprador);
            setProveedor(res.data.proveedor);
            setServicio(res.data.servicioActual);
            setTextoRes(res.data.reservaActual?.reclamo?.resolucion !== "NA" ? (res.data.reservaActual?.reclamo?.resolucion || "") : "");
        } catch {
            Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar el reclamo.", confirmButtonColor: "var(--azul-logo-fondo)" });
            navigate(-1);
        } finally {
            setCargando(false);
        }
    }

    useEffect(() => {
        if (!idReserva) {
            Swal.fire({ icon: "error", title: "Sin datos", text: "No se especificó el reclamo.", confirmButtonColor: "var(--azul-logo-fondo)" });
            navigate(-1);
            return;
        }
        cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idReserva]);

    async function ejecutarAccion(accion, confirmar) {
        if (confirmar) {
            const { isConfirmed } = await Swal.fire({
                title: confirmar.titulo,
                text: confirmar.texto,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: confirmar.boton || "Confirmar",
                cancelButtonText: "Cancelar",
                confirmButtonColor: confirmar.color || "var(--azul-logo-fondo)",
            });
            if (!isConfirmed) return;
        }
        try {
            setGuardando(true);
            const res = await axios.post("/gestionar-reclamo-admin", { params: { idReserva, accion, resolucion: textoRes || undefined } });
            if (res.data.exito) {
                await cargar();
                Swal.fire({ icon: "success", title: "Actualizado", text: res.data.mensaje, confirmButtonColor: "var(--azul-logo-fondo)", timer: 2000, showConfirmButton: false });
            } else {
                Swal.fire({ icon: "error", title: "Error", text: res.data.mensaje || "No se pudo actualizar.", confirmButtonColor: "var(--azul-logo-fondo)" });
            }
        } catch {
            Swal.fire({ icon: "error", title: "Error", text: "Error al actualizar el reclamo.", confirmButtonColor: "var(--azul-logo-fondo)" });
        } finally {
            setGuardando(false);
        }
    }

    if (cargando) return <ZonaCargando />;
    if (!reserva) return null;

    const reclamo    = reserva.reclamo    || {};
    const desembolso = reserva.desembolso || {};
    const cobro      = reserva.cobro      || {};

    const cfgReclamo    = ESTADO_RECLAMO[reclamo.estado]      || ESTADO_RECLAMO.abierto;
    const cfgDesembolso = ESTADO_DESEMBOLSO[desembolso.estado] || ESTADO_DESEMBOLSO.pendiente;

    const estaActivo  = ["abierto", "en_revision"].includes(reclamo.estado);
    const estaAbierto = reclamo.estado === "abierto";
    const estaResuelto = ["resuelto_a_favor_comprador", "resuelto_a_favor_proveedor", "cerrado"].includes(reclamo.estado);

    return (
        <div className="container-fluid">
            {/* Cabecera */}
            <div className="d-flex justify-content-between align-items-start pt-3 pb-2 mb-4 border-bottom flex-wrap gap-2">
                <div>
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <h1 className="h2 text-dark mb-0">Reclamo</h1>
                        <span className={`badge rounded-pill px-3 ${cfgReclamo.cls}`} style={{ fontSize: "0.78rem" }}>
                            <i className={`bi ${cfgReclamo.icono} me-1`}></i>{cfgReclamo.label}
                        </span>
                    </div>
                    <p className="text-muted small mb-0" style={{ fontFamily: "monospace", fontSize: "0.72rem" }}>Reserva: {idReserva}</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-outline-secondary btn-sm rounded-3" onClick={() => navigate(-1)}>
                        <i className="bi bi-arrow-left me-1"></i>Volver
                    </button>
                    <button className="btn btn-outline-secondary btn-sm rounded-3"
                        onClick={() => navigate("/admin/reserva-admin", { state: { params: { idReserva } } })}>
                        <i className="bi bi-calendar-check me-1"></i>Ver reserva
                    </button>
                    {cobro?.estadoPago === "APPROVED" && (
                        <button className="btn btn-outline-primary btn-sm rounded-3"
                            onClick={() => navigate("/admin/pago", { state: { params: { idReserva } } })}>
                            <i className="bi bi-credit-card me-1"></i>Ver pago
                        </button>
                    )}
                </div>
            </div>

            <div className="row g-4">
                {/* Izquierda */}
                <div className="col-12 col-lg-7 d-flex flex-column gap-4">

                    {/* Bloque reclamo */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.05em" }}>
                                <i className="bi bi-exclamation-triangle-fill me-1 text-danger"></i>Detalle del reclamo
                            </p>
                            <Fila label="Estado">
                                <span className={`badge rounded-pill px-2 py-1 ${cfgReclamo.cls}`} style={{ fontSize: "0.72rem" }}>
                                    <i className={`bi ${cfgReclamo.icono} me-1`}></i>{cfgReclamo.label}
                                </span>
                            </Fila>
                            <Fila label="Motivo"        value={val(reclamo.motivo)} />
                            <Fila label="Fecha apertura" value={fmtF(reclamo.fechaApertura)} />
                            {val(reclamo.fechaCierre) && <Fila label="Fecha cierre" value={fmtF(reclamo.fechaCierre)} />}
                            {val(reclamo.resolucion) && (
                                <Fila label="Resolución">
                                    <span className="small text-dark fw-semibold" style={{ maxWidth: 300, textAlign: "right" }}>{reclamo.resolucion}</span>
                                </Fila>
                            )}

                            {reclamo.descripcion && reclamo.descripcion !== "NA" && (
                                <div className="mt-3 p-3 rounded-3" style={{ background: "var(--color-track-bg)", border: "1px solid var(--color-track-bg)" }}>
                                    <div className="text-muted small fw-semibold mb-1">Descripción del comprador</div>
                                    <div className="small text-dark" style={{ lineHeight: 1.6 }}>{reclamo.descripcion}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Servicio */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <p className="text-muted small fw-semibold text-uppercase mb-2" style={{ letterSpacing: "0.05em" }}>
                                <i className="bi bi-briefcase me-1"></i>Servicio
                            </p>
                            <div className="d-flex align-items-start justify-content-between gap-3">
                                <div>
                                    <div className="fw-bold text-dark">{reserva.nombreServicio || servicio?.nombre || "—"}</div>
                                    <div className="text-muted small mt-1 d-flex gap-3 flex-wrap">
                                        {val(reserva.tipoPaquete) && <span><i className="bi bi-box me-1"></i>{reserva.tipoPaquete}</span>}
                                        {val(servicio?.categoria) && <span><i className="bi bi-tag me-1"></i>{servicio.categoria}</span>}
                                    </div>
                                </div>
                                {val(reserva.idServicio) && (
                                    <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill px-3 flex-shrink-0"
                                        onClick={() => {
                                            localStorage.setItem("admin_nav_servicio", JSON.stringify({ idServicio: reserva.idServicio }));
                                            window.open("/admin/servicio", "_blank");
                                        }}>
                                        <i className="bi bi-box-arrow-up-right me-1"></i>Ver servicio
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Acciones admin */}
                    {!estaResuelto && (
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.05em" }}>
                                    <i className="bi bi-sliders me-1"></i>Gestión del reclamo
                                </p>

                                <div className="mb-3">
                                    <label className="form-label small fw-semibold text-dark">Resolución / notas internas</label>
                                    <textarea
                                        className="form-control rounded-3 small"
                                        rows={3}
                                        placeholder="Describe la decisión tomada o las notas del caso…"
                                        value={textoRes}
                                        onChange={e => setTextoRes(e.target.value)}
                                    />
                                </div>

                                <div className="d-flex flex-wrap gap-2">
                                    {estaAbierto && (
                                        <button className="btn btn-sm btn-warning rounded-3 fw-semibold" disabled={guardando}
                                            onClick={() => ejecutarAccion("en_revision")}>
                                            <i className="bi bi-search me-1"></i>Marcar en revisión
                                        </button>
                                    )}
                                    {estaActivo && (
                                        <>
                                            <button className="btn btn-sm btn-primary rounded-3 fw-semibold" disabled={guardando}
                                                onClick={() => ejecutarAccion("resolver_comprador", {
                                                    titulo: "¿Resolver a favor del comprador?",
                                                    texto: "El desembolso al vendedor quedará retenido. Esta acción no se puede deshacer fácilmente.",
                                                    boton: "Sí, resolver a favor del comprador",
                                                    color: "var(--azul-logo-claro)",
                                                })}>
                                                <i className="bi bi-person-check-fill me-1"></i>Resolver → Comprador
                                            </button>
                                            <button className="btn btn-sm btn-success rounded-3 fw-semibold" disabled={guardando}
                                                onClick={() => ejecutarAccion("resolver_proveedor", {
                                                    titulo: "¿Resolver a favor del vendedor?",
                                                    texto: "El desembolso volverá a estado 'Pendiente' para ser pagado al vendedor.",
                                                    boton: "Sí, resolver a favor del vendedor",
                                                    color: "var(--verde-esmeralda)",
                                                })}>
                                                <i className="bi bi-person-fill-check me-1"></i>Resolver → Vendedor
                                            </button>
                                            <button className="btn btn-sm btn-outline-secondary rounded-3 fw-semibold" disabled={guardando}
                                                onClick={() => ejecutarAccion("cerrar", {
                                                    titulo: "¿Cerrar el reclamo?",
                                                    texto: "Se cerrará sin cambiar el estado del desembolso. Puedes ajustar el desembolso manualmente.",
                                                    boton: "Cerrar reclamo",
                                                })}>
                                                <i className="bi bi-x-circle me-1"></i>Cerrar
                                            </button>
                                        </>
                                    )}
                                    {textoRes && (
                                        <button className="btn btn-sm btn-outline-dark rounded-3 fw-semibold ms-auto" disabled={guardando}
                                            onClick={() => ejecutarAccion("actualizar_resolucion")}>
                                            <i className="bi bi-floppy me-1"></i>Guardar notas
                                        </button>
                                    )}
                                </div>
                                {guardando && (
                                    <div className="text-muted small mt-2">
                                        <span className="spinner-border spinner-border-sm me-1"></span>Guardando…
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Desembolso */}
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <p className="text-muted small fw-semibold text-uppercase mb-0" style={{ letterSpacing: "0.05em" }}>
                                    <i className="bi bi-bank2 me-1"></i>Desembolso al vendedor
                                </p>
                                <span className={`badge rounded-pill px-3 py-2 ${cfgDesembolso.cls}`} style={{ fontSize: "0.75rem" }}>{cfgDesembolso.label}</span>
                            </div>
                            <Fila label="Monto proveedor" value={fmtM(desembolso.montoProveedor)} />
                            <Fila label="Disponible desde" value={fmtF(desembolso.disponibleDesde)} />
                            <Fila label="Fecha programada" value={fmtF(desembolso.fechaProgramada)} />
                            {val(desembolso.notas) && <Fila label="Notas" value={desembolso.notas} />}
                        </div>
                    </div>
                </div>

                {/* Derecha */}
                <div className="col-12 col-lg-5 d-flex flex-column gap-4">

                    {/* Pago */}
                    {cobro?.estadoPago === "APPROVED" && (
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.05em" }}>
                                    <i className="bi bi-credit-card me-1"></i>Pago
                                </p>
                                <Fila label="Total cobrado" value={fmtM(cobro.montoTotal)} />
                                <Fila label="Pasarela"     value={val(cobro.gateway) || "Wompi"} />
                                <Fila label="Método"       value={val(cobro.metodoPagoTipo)} />
                                <Fila label="Fecha pago"   value={fmtF(cobro.fechaPago)} />
                            </div>
                        </div>
                    )}

                    <TarjetaUsuario u={comprador} rol="Comprador (abrió el reclamo)" />
                    <TarjetaUsuario u={proveedor}  rol="Vendedor" />
                </div>
            </div>
        </div>
    );
}

export default ReclamoIndividual;
